import { Dropdown } from "@src/components/dropdown/Dropdown";
import { FilterField } from "@src/components/filtersPanel/FilterField";
import { FilterSection } from "@src/components/filtersPanel/FilterSection";
import styles from "@src/components/filtersPanel/FilterLayout.module.scss";
import { DateInput } from "@src/components/input/DateInput";
import { Input } from "@src/components/input/Input";
import { SelectYup } from "@src/components/select/SelectYup";
import { salesFiltersEmptyValues } from "@src/components/utils/hookFormUtils";
import { SalesFiltersProps } from "@src/validation/SalesHeaderFiltersValidation";
import React, { useState } from "react";
import { Control, UseFormGetValues, UseFormReset, UseFormSetValue } from "react-hook-form";

interface ICategoriesGeneric {
	label: string;
	value: string | number;
}

interface IAllOptions {
	categoryOptions: Array<ICategoriesGeneric>;
	headlinerOptions: Array<ICategoriesGeneric>;
	venueOptions: Array<ICategoriesGeneric>;
	marketplaceOptions: Array<ICategoriesGeneric>;
	saleStatusesOptions: Array<ICategoriesGeneric>;
	tagOptions: Array<ICategoriesGeneric>;
	companyOptions: Array<ICategoriesGeneric>;
}

interface IAllFilters extends IAllOptions {
	control: Control;
	setValue: UseFormSetValue<SalesFiltersProps>;
	getValues: UseFormGetValues<SalesFiltersProps>;
	optionsLoading: Record<string, boolean>;
	reset: UseFormReset<SalesFiltersProps>;
	savedFilters?: Array<SalesFiltersProps>;
	companiesLoading: boolean;
	isAdmin: boolean;
	onSaveFilters: () => void;
}

export const SalesFilters: React.FC<IAllFilters> = ({
	control,
	setValue,
	getValues,
	optionsLoading,
	reset,
	savedFilters,
	categoryOptions,
	headlinerOptions,
	venueOptions,
	tagOptions,
	marketplaceOptions,
	saleStatusesOptions,
	companyOptions,
	companiesLoading,
	isAdmin,
	onSaveFilters,
}) => {
	const [isAdvancedOpen, setIsAdvancedOpen] = useState(true);

	const savedSearchItems: Array<{ id: number; label: string; onClick: () => void }> = [
		{ id: 0, label: "Select", onClick: () => reset({ ...salesFiltersEmptyValues, companyName: getValues("companyName") }) },
		{ id: 1, label: "Save current filters", onClick: onSaveFilters },
		...(savedFilters ?? []).map((item, index) => ({
			id: index + 2,
			label: `Saved Filters ${index + 1}`,
			onClick: () => {
				reset(salesFiltersEmptyValues);
				(Object.entries(item) as Array<[keyof SalesFiltersProps, string]>).forEach(
					([key, value]) => value && setValue(key, value, { shouldDirty: true }),
				);
			},
		})),
	];

	return (
		<>
			<FilterSection title="Search">
				<FilterField label="Smart search">
					<Input type="text" control={control} name="smartSearch" placeholder="Search by any parameter" />
				</FilterField>

				{isAdmin ? (
					<FilterField label="Company name">
						<SelectYup
							isLoading={companiesLoading}
							name="companyName"
							placeholder="Search by company name"
							control={control}
							options={companyOptions}
						/>
					</FilterField>
				) : null}

				<FilterField label="Tags">
					<SelectYup
						name="tags"
						placeholder="Search by tags"
						control={control}
						options={tagOptions}
						isLoading={optionsLoading.tags}
					/>
				</FilterField>
			</FilterSection>

			<FilterSection title="Event">
				<div className={styles.row}>
					<FilterField label="Event start">
						<DateInput
							popperStrategy="fixed"
							control={control}
							name="eventFromDate"
							placeholder="Start date"
							popperPlacement="bottom-start"
						/>
					</FilterField>
					<FilterField label="Event end">
						<DateInput
							popperStrategy="fixed"
							control={control}
							name="eventToDate"
							placeholder="End date"
							popperPlacement="bottom-end"
						/>
					</FilterField>
				</div>

				<FilterField label="Event category">
					<SelectYup
						name="category"
						placeholder="Select event category"
						control={control}
						options={categoryOptions}
						isLoading={optionsLoading.category}
					/>
				</FilterField>

				<FilterField label="Event headliner">
					<SelectYup
						name="headliner"
						placeholder="Select event headliner"
						control={control}
						options={headlinerOptions}
						isLoading={optionsLoading.headliner}
					/>
				</FilterField>

				<FilterField label="Event venue">
					<SelectYup
						name="venue"
						placeholder="Select event venue"
						control={control}
						options={venueOptions}
						isLoading={optionsLoading.venue}
					/>
				</FilterField>
			</FilterSection>

			<FilterSection title="Sales">
				<FilterField label="Marketplace">
					<SelectYup
						name="exchangeName"
						placeholder="Choose marketplace"
						control={control}
						options={marketplaceOptions}
						isLoading={optionsLoading.exchangeName}
					/>
				</FilterField>

				<FilterField label="Sale status">
					<SelectYup
						name="saleStatus"
						placeholder="Choose sales status"
						control={control}
						options={saleStatusesOptions}
						isLoading={optionsLoading.saleStatus}
					/>
				</FilterField>

				<div className={styles.row}>
					<FilterField label="Sale start">
						<DateInput
							popperStrategy="fixed"
							control={control}
							name="saleFromDate"
							placeholder="Start date"
							popperPlacement="bottom-start"
						/>
					</FilterField>
					<FilterField label="Sale end">
						<DateInput
							popperStrategy="fixed"
							control={control}
							name="saleToDate"
							placeholder="End date"
							popperPlacement="bottom-end"
						/>
					</FilterField>
				</div>

				<div className={styles.row}>
					<FilterField label="Payment start">
						<DateInput
							popperStrategy="fixed"
							control={control}
							name="paymentFromDate"
							placeholder="Start date"
							popperPlacement="bottom-start"
						/>
					</FilterField>
					<FilterField label="Payment end">
						<DateInput
							popperStrategy="fixed"
							control={control}
							name="paymentToDate"
							placeholder="End date"
							popperPlacement="bottom-end"
						/>
					</FilterField>
				</div>
			</FilterSection>

			<FilterSection title="Ticket">
				<div className={styles.row}>
					<FilterField label="Section">
						<Input control={control} name="sections" placeholder="Enter ticket section" />
					</FilterField>
					<FilterField label="Row">
						<Input control={control} name="rows" placeholder="Enter ticket row" />
					</FilterField>
				</div>

				<FilterField label="Minimum quantity">
					<Input control={control} type="number" name="minQuantity" placeholder="Enter minimum ticket quantity" />
				</FilterField>
			</FilterSection>

			<FilterSection
				title="Advanced"
				isCollapsible
				isOpen={isAdvancedOpen}
				onToggle={() => setIsAdvancedOpen((prev) => !prev)}>
				<div className={styles.row}>
					<FilterField label="Invoice ID">
						<Input name="invoiceId" placeholder="Enter invoice ID" control={control} />
					</FilterField>
					<FilterField label="Event ID">
						<Input name="eventId" placeholder="Enter event ID" control={control} />
					</FilterField>
				</div>

				<FilterField label="Buyer PO ID">
					<Input name="buyerPOId" placeholder="Enter buyer PO ID" control={control} />
				</FilterField>
			</FilterSection>

			<div className={styles.savedSearch}>
				<div className={styles.fieldLabel}>Saved search</div>
				<Dropdown
					dynamicText
					isDisabled={false}
					buttonText="Select saved search"
					containerWidth="100%"
					items={savedSearchItems}
				/>
			</div>
		</>
	);
};
